// Singles Chain — useSinglesReserve hook.
//
// Read-only consumer of get_sc_reserve() RPC.
// Returns the user's capital/profit split + recent transactions.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

// ─── Types ────────────────────────────────────────────────

export interface ScReserve {
  id: string;
  capital_balance: number;
  profit_balance: number;
  total_balance: number;
  rollover_pct: number;
  status: "active" | "paused" | "depleted";
}

export interface ScTransaction {
  id: string;
  tx_type: string;
  amount: number;
  capital_after: number;
  profit_after: number;
  note: string | null;
  created_at: string;
}

export interface UseSinglesReserveState {
  reserve: ScReserve | null;
  transactions: readonly ScTransaction[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// ─── Hook ─────────────────────────────────────────────────

export function useSinglesReserve(): UseSinglesReserveState {
  const { user } = useAuthSession();
  const [reserve, setReserve] = useState<ScReserve | null>(null);
  const [transactions, setTransactions] = useState<readonly ScTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!user) {
      setReserve(null);
      setTransactions([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const { data, error: rpcError } = await (
          client.rpc as unknown as (fn: string) => Promise<{
            data: Record<string, unknown> | null;
            error: { message: string } | null;
          }>
        )("get_sc_reserve");

        if (cancelled) return;

        if (rpcError) {
          setError(rpcError.message);
          setLoading(false);
          return;
        }

        if (!data) {
          setReserve(null);
          setTransactions([]);
          setLoading(false);
          return;
        }

        const raw = data as Record<string, unknown>;

        if (raw.reserve && typeof raw.reserve === "object") {
          const r = raw.reserve as Record<string, unknown>;
          setReserve({
            id: String(r.id ?? ""),
            capital_balance: Number(r.capital_balance ?? 0),
            profit_balance: Number(r.profit_balance ?? 0),
            total_balance: Number(r.total_balance ?? 0),
            rollover_pct: Number(r.rollover_pct ?? 0.30),
            status: (r.status as ScReserve["status"]) ?? "active",
          });
        } else {
          setReserve(null);
        }

        if (Array.isArray(raw.recent_transactions)) {
          setTransactions(
            (raw.recent_transactions as Record<string, unknown>[]).map((t) => ({
              id: String(t.id ?? ""),
              tx_type: String(t.tx_type ?? ""),
              amount: Number(t.amount ?? 0),
              capital_after: Number(t.capital_after ?? 0),
              profit_after: Number(t.profit_after ?? 0),
              note: typeof t.note === "string" ? t.note : null,
              created_at: String(t.created_at ?? ""),
            })),
          );
        } else {
          setTransactions([]);
        }

        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user, tick]);

  return useMemo(
    () => ({ reserve, transactions, loading, error, refetch }),
    [reserve, transactions, loading, error, refetch],
  );
}
